import React, { useState, useEffect } from 'react';
import axios from 'axios';


const API_URL = process.env.REACT_APP_API_URL;
const PointPurchase = ({ user, updateUserPoints }) => {
  const [settings, setSettings] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState('');
  const [calculatedPoints, setCalculatedPoints] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (amount && settings) {
      calculatePoints();
    }
  }, [amount, settings]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [settingsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchase/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API_URL}/api/purchase/history`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      setSettings(settingsRes.data);
      setHistory(historyRes.data.transactions || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const calculatePoints = async () => {
    if (!amount || !settings) return;
    try {
      const yen = Number(amount);
      if (!Number.isFinite(yen) || yen <= 0) {
        setCalculatedPoints(0);
        return;
      }
      const rate = settings.exchangeRate.yenAmount / settings.exchangeRate.pointAmount;
      const pts = Math.floor(yen / rate);
      setCalculatedPoints(pts);
    } catch (err) {
      console.error('Error calculating points:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || calculatedPoints <= 0) {
      alert('正しい金額を入力してください');
      return;
    }
    if (!window.confirm(`${amount}円で${calculatedPoints}ポイントを購入しますか？`)) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/purchase`, {
        paymentMethod,
        amount: parseFloat(amount),
        points: calculatedPoints
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setCurrentTransaction(response.data.transaction);
      setShowSuccessModal(true);
      setAmount('');
      setCalculatedPoints(0);
      fetchInitialData();
    } catch (err) {
      console.error('Error creating purchase:', err);
      alert(err.response?.data?.message || '購入リクエストの作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'paid': return '#27ae60';
      case 'cancelled': return '#e74c3c';
      case 'failed': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '処理中';
      case 'paid': return '支払い済み';
      case 'cancelled': return 'キャンセル';
      case 'failed': return '失敗';
      default: return status;
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>読み込み中...</div>;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 20 }}>
      <h2>ポイント購入</h2>
      {settings && (
        <div style={{ 
          background: '#e8f5e8', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #27ae60'
        }}>
          <strong>現在のレート:</strong> {settings.exchangeRate.yenAmount}円 = {settings.exchangeRate.pointAmount}ポイント
        </div>
      )}

      {/* Purchase Form */}
      <form onSubmit={handleSubmit} style={{ 
        background: '#f8f8f8', 
        padding: '25px', 
        borderRadius: '8px', 
        marginBottom: '30px',
        border: '1px solid #ddd'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            支払い方法：
          </label>
          <select 
            value={paymentMethod} 
            onChange={e => setPaymentMethod(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '10px', 
              borderRadius: '4px', 
              border: '1px solid #ddd' 
            }}
          >
            {/* <option value="bank_transfer">銀行振込</option> */}
            <option value="smart_pit">SmartPit</option>
          </select>
        </div>

        {settings && (
          <div style={{ 
            background: '#fff', 
            padding: '15px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            border: '1px solid #eee'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>
              {paymentMethod === 'bank_transfer' ? '銀行振込' : 'SmartPit'} 詳細
            </h4>
            {paymentMethod === 'bank_transfer' ? (
              <div>
                <p><strong>口座名義:</strong> {settings.bankTransfer.accountName}</p>
                <p><strong>口座番号:</strong> {settings.bankTransfer.accountNumber}</p>
                <p><strong>銀行名:</strong> {settings.bankTransfer.bankName}</p>
                <p><strong>支店名:</strong> {settings.bankTransfer.branchName}</p>
                <p><strong>口座種別:</strong> {settings.bankTransfer.accountType}</p>
                <p style={{ color: '#7f8c8d' }}>お振込み後、管理者が確認次第、ポイントが付与されます。</p>
              </div>
            ) : (
              <div>
                <p><strong>SmartPit ID:</strong> {settings.smartPit.smartPitId}</p>
                <p><em>購入確定後、支払いコードとバーコードが表示されます。コンビニ端末で支払いください。</em></p>
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            購入金額（円）：
          </label>
          <input 
            type="number" 
            value={amount} 
            onChange={e => setAmount(e.target.value)} 
            min={settings?.limits?.minimum || 1000}
            max={settings?.limits?.maximum || 100000}
            style={{ 
              width: '100%', 
              padding: '10px', 
              borderRadius: '4px', 
              // border: '1px solid '#ddd' 
            }}
          />
          {settings && (
            <small style={{ color: '#7f8c8d' }}>
              {settings.limits.minimum}円〜{settings.limits.maximum}円の間で入力してください
            </small>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            獲得ポイント：
          </label>
          <span style={{ 
            fontWeight: 'bold', 
            fontSize: '18px', 
            color: '#27ae60',
            padding: '10px',
            background: '#e8f5e8',
            borderRadius: '4px',
            display: 'inline-block'
          }}>
            {calculatedPoints.toLocaleString()} pt
          </span>
        </div>

        <button 
          type="submit" 
          disabled={submitting || !amount || calculatedPoints <= 0}
          style={{
            background: submitting ? '#95a5a6' : '#3498db',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '4px',
            cursor: submitting ? 'not-allowed' : 'pointer',
            fontSize: '16px'
          }}
        >
          {submitting ? '送信中...' : '購入リクエスト送信'}
        </button>
      </form>
 
      {/* Purchase History */}
      {/*<h3>購入履歴</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          background: '#fff', 
          borderRadius: '8px', 
          overflow: 'hidden',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{ background: '#34495e', color: 'white' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>日時</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>取引ID</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>方法</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>金額</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>ポイント</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>ステータス</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
                  購入履歴がありません
                </td>
              </tr>
            )}
            {history.map(transaction => (
              <tr key={transaction._id} style={{ borderBottom: '1px solid #ecf0f1' }}>
                <td style={{ padding: '12px' }}>
                  {new Date(transaction.createdAt).toLocaleString('ja-JP')}
                </td>
                <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>
                  {transaction.transactionId}
                </td>
                <td style={{ padding: '12px' }}>
                  {transaction.paymentMethod === 'bank_transfer' ? '銀行振込' : 'SmartPit'}
                </td>
                <td style={{ padding: '12px', fontWeight: 'bold' }}>
                  ¥{transaction.amount.toLocaleString()}
                </td>
                <td style={{ padding: '12px', fontWeight: 'bold', color: '#27ae60' }}>
                  {transaction.points.toLocaleString()} pt
                </td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    backgroundColor: getStatusColor(transaction.status),
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {getStatusText(transaction.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> */}

      {/* Success Modal */}
      {showSuccessModal && currentTransaction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ color: '#27ae60', marginBottom: '20px' }}>購入リクエスト完了</h3>
            <div style={{ marginBottom: '20px' }}>
              <p><strong>取引ID:</strong> {currentTransaction.transactionId}</p>
              <p><strong>支払い方法:</strong> {currentTransaction.paymentMethod === 'bank_transfer' ? '銀行振込' : 'SmartPit'}</p>
              <p><strong>金額:</strong> ¥{currentTransaction.amount.toLocaleString()}</p>
              <p><strong>ポイント:</strong> {currentTransaction.points.toLocaleString()} pt</p>
            </div>

            {currentTransaction.paymentMethod === 'smart_pit' && (
              <div style={{ 
                background: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '4px', 
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <h4 style={{ margin: '0 0 10px 0' }}>SmartPit支払い情報</h4>
                <p style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  color: '#e67e22',
                  textAlign: 'center',
                  padding: '10px',
                  background: 'white',
                  borderRadius: '4px'
                }}>
                  {currentTransaction.smartPitDetails?.paymentCode}
                </p>
                {currentTransaction.smartPitDetails?.barcode && (
                  <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(currentTransaction.smartPitDetails.barcode)}`}
                      alt="SmartPit Barcode"
                      style={{ width: 180, height: 180 }}
                    />
                  </div>
                )}
                <p style={{ fontSize: '14px', color: '#7f8c8d', margin: '10px 0 0 0' }}>
                  このコードをコンビニで提示して支払いを行ってください。
                </p>
              </div>
            )}

            <button 
              onClick={() => setShowSuccessModal(false)}
              style={{
                background: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointPurchase; 